import React, { useState } from "react";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Separator } from "~/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

interface DocumentDetail {
  id: number;
  lineNumber: number;
  quantity: number;
  netUnitValue: number;
  netAmount: number;
  variant: {
    description: string;
    code: string;
  };
}

export interface PartialCreditItem {
  document_detail_id: number;
  quantity: number;
  unit_value?: number;
}

interface PartialCreditNoteSelectorProps {
  documentDetails: DocumentDetail[];
  selectedItems: PartialCreditItem[];
  onItemsChange: (items: PartialCreditItem[]) => void;
  disabled?: boolean;
}

export const PartialCreditNoteSelector: React.FC<
  PartialCreditNoteSelectorProps
> = ({ documentDetails, selectedItems, onItemsChange, disabled = false }) => {
  const [editingItem, setEditingItem] = useState<number | null>(null);

  const handleQuantityChange = (detailId: number, newQuantity: number) => {
    const detail = documentDetails.find((d) => d.id === detailId);
    if (!detail) return;

    if (newQuantity <= 0) {
      // Remove item if quantity is 0 or negative
      onItemsChange(
        selectedItems.filter((item) => item.document_detail_id !== detailId)
      );
    } else if (newQuantity <= detail.quantity) {
      // Update existing item or add new one
      const existingIndex = selectedItems.findIndex(
        (item) => item.document_detail_id === detailId
      );
      if (existingIndex >= 0) {
        const updated = [...selectedItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: newQuantity,
        };
        onItemsChange(updated);
      } else {
        onItemsChange([
          ...selectedItems,
          {
            document_detail_id: detailId,
            quantity: newQuantity,
            unit_value: detail.netUnitValue,
          },
        ]);
      }
    }
  };

  const handleUnitValueChange = (detailId: number, newUnitValue: number) => {
    console.log(`🔍 Unit value change for detail ${detailId}: ${newUnitValue}`);

    const existingIndex = selectedItems.findIndex(
      (item) => item.document_detail_id === detailId
    );

    if (existingIndex >= 0) {
      const updated = [...selectedItems];
      updated[existingIndex] = {
        ...updated[existingIndex],
        unit_value: newUnitValue,
      };
      console.log(`🔍 Updated item:`, updated[existingIndex]);
      onItemsChange(updated);
    } else {
      console.log(`🔍 Item ${detailId} not found in selectedItems`);
    }
  };

  const getSelectedQuantity = (detailId: number): number => {
    const item = selectedItems.find(
      (item) => item.document_detail_id === detailId
    );
    return item?.quantity || 0;
  };

  const getSelectedUnitValue = (detailId: number): number => {
    const item = selectedItems.find(
      (item) => item.document_detail_id === detailId
    );
    return item?.unit_value || 0;
  };

  const getTotalCreditAmount = (): number => {
    return selectedItems.reduce((total, item) => {
      return total + item.quantity * (item.unit_value || 0);
    }, 0);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Seleccionar Productos para Nota de Crédito Parcial
        </CardTitle>
        <CardDescription>
          Elige los productos y cantidades para incluir en la nota de crédito
          parcial
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Producto</TableHead>
                <TableHead className="text-right">Cantidad Original</TableHead>
                <TableHead className="text-right">Valor Unitario</TableHead>
                <TableHead className="text-right">
                  Cantidad a Creditar
                </TableHead>
                <TableHead className="text-right">Valor Unitario NC</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="w-[100px]">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documentDetails.map((detail, index) => {
                const selectedQuantity = getSelectedQuantity(detail.id);
                const selectedUnitValue = getSelectedUnitValue(detail.id);
                const isEditing = editingItem === detail.id;
                const isSelected = selectedQuantity > 0;

                return (
                  <TableRow
                    key={detail.id}
                    style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
                    className={`duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both ${isSelected ? "bg-muted/50" : ""}`}
                  >
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {detail.variant.description}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          Código: {detail.variant.code}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {detail.quantity}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(detail.netUnitValue)}
                    </TableCell>
                    <TableCell className="text-right">
                      {isEditing || isSelected ? (
                        <Input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          max={detail.quantity}
                          value={selectedQuantity}
                          onChange={(e) =>
                            handleQuantityChange(
                              detail.id,
                              Number(e.target.value)
                            )
                          }
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") setEditingItem(null);
                          }}
                          className="w-20 text-right"
                          autoFocus={isEditing}
                          disabled={disabled}
                        />
                      ) : (
                        <span
                          className={`cursor-pointer rounded px-2 py-1 hover:bg-muted ${isSelected ? "font-medium" : ""}`}
                          onClick={() => !disabled && setEditingItem(detail.id)}
                        >
                          {selectedQuantity || "-"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isSelected ? (
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.01"
                          min="0"
                          value={selectedUnitValue}
                          onChange={(e) =>
                            handleUnitValueChange(
                              detail.id,
                              Number(e.target.value)
                            )
                          }
                          onFocus={(e) => e.target.select()}
                          className="w-24 text-right"
                          disabled={disabled}
                        />
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isSelected ? (
                        <span className="font-medium">
                          {formatCurrency(selectedQuantity * selectedUnitValue)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isSelected ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuantityChange(detail.id, 0)}
                          disabled={disabled}
                        >
                          Quitar
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingItem(detail.id);
                            handleQuantityChange(detail.id, 1);
                          }}
                          disabled={disabled}
                        >
                          Agregar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {selectedItems.length > 0 && (
            <>
              <Separator />
              <div className="flex items-center justify-between rounded-lg border bg-muted/40 p-4">
                <div>
                  <div className="font-medium">
                    Total de la Nota de Crédito Parcial
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedItems.length} producto(s) seleccionado(s)
                  </div>
                </div>
                <div className="text-xl font-bold">
                  {formatCurrency(getTotalCreditAmount())}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
